import type { LabelInValue, NodeOrganization } from 'models';
import { type ComponentProps, useMemo } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useSearchDebounce } from 'common/hooks';
import { useUser } from 'features/auth';
import { useNodeOrganizationsInfiniteQuery } from '../queries';

type P<T extends LabelInValue> = {
  createOption: (option: NodeOrganization) => T;
  query?: Partial<Parameters<typeof useNodeOrganizationsInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const NodeOrganizationPicker = <T extends LabelInValue>({ createOption, query, ...selectProperties }: P<T>) => {
  const {
    nodeUserData: { _id },
  } = useUser();
  const { isDebouncing, searchDebounced, setSearch } = useSearchDebounce({
    initialValue: query?.filter?.name,
  });

  const {
    data: organizations,
    fetchNextPage: fetchNextPageOfOrganizations,
    hasNextPage: hasMoreOrganizations,
    isFetching: isFetchingOrganizations,
    isRefetching,
  } = useNodeOrganizationsInfiniteQuery({
    ...query,
    filter: { ...query?.filter, admin: _id, name: searchDebounced },
    pagination: { pageSize: 10, ...query?.pagination },
  });

  const allFetchedOrganizations = useMemo(
    () => organizations?.pages.flatMap((page) => page.data.map((option) => createOption(option))),
    [organizations, createOption],
  );

  return (
    <InfinityScrollSelect<T>
      allowClear
      handleRequestMoreData={fetchNextPageOfOrganizations}
      hasMoreData={hasMoreOrganizations}
      isRefetching={isRefetching}
      loading={isFetchingOrganizations || isDebouncing}
      onSearch={setSearch}
      options={allFetchedOrganizations}
      placeholder="Choose organization..."
      {...selectProperties}
    />
  );
};

export default NodeOrganizationPicker;
